import { z } from "zod";
/**
 * The realtime wire protocol.
 *
 * One WebSocket per user, multiplexed across every channel they can see —
 * not one socket per channel. Browsers cap connections per origin, and a
 * user sitting in a dozen channels would exhaust that budget for no gain:
 * the server already knows which channels they belong to.
 *
 * Every frame is a JSON object with a `t` discriminator. Client frames are
 * validated with these schemas before anything touches them; server frames
 * are typed but not re-validated on arrival, since the client trusts its
 * own server and the cost is per-message.
 */
/* ───────────────────────────── client → server ───────────────────────────── */
/** Liveness. The client answers `ping` with `pong` so a half-open TCP
 * connection (laptop lid closed, phone off wifi) is detected within one
 * heartbeat rather than lingering as a phantom subscriber. */
export const clientPing = z.object({ t: z.literal("ping") });
/**
 * Announces which channel the user is currently looking at. Typing indicators
 * and presence are scoped to this, so a user with 30 channels open does not
 * receive typing noise from all of them.
 */
export const clientFocus = z.object({
    t: z.literal("focus"),
    channelId: z.string().uuid().nullable(),
});
/** Typing indicator. Fire-and-forget; the server expires it on a timer rather
 * than trusting the client to send a matching "stopped typing". */
export const clientTyping = z.object({
    t: z.literal("typing"),
    channelId: z.string().uuid(),
});
export const clientFrame = z.discriminatedUnion("t", [
    clientPing,
    clientFocus,
    clientTyping,
]);
/* ─────────────────────────────── tuning ─────────────────────────────── */
/**
 * How long a typing indicator lives on screen without a refresh.
 *
 * The client expires the bubble after this; the server never sends a
 * "stopped typing" frame, because a client that crashes mid-word would never
 * send one and the bubble would stick forever.
 */
export const TYPING_TTL_MS = 4_000;
/**
 * How often a continuing typist is re-broadcast.
 *
 * Deliberately shorter than {@link TYPING_TTL_MS}: the two together mean a
 * client that connects part-way through someone's sentence waits at most this
 * long to see the indicator, and an existing viewer's bubble is refreshed
 * before it expires. Setting this equal to the TTL — as a naive "one broadcast
 * per display window" throttle would — leaves a late joiner staring at nothing
 * for up to a full window, which reads as a broken feature.
 */
export const TYPING_REBROADCAST_MS = 1_500;
/** Server heartbeat interval. A socket that misses two consecutive pings is
 * terminated — long enough to survive a brief stall, short enough that a dead
 * connection is not still counted as "online" a minute later. */
export const HEARTBEAT_INTERVAL_MS = 25_000;
/**
 * Close code for "there is no session behind this socket" — never had one, or
 * it ended (signed out, revoked, expired). 4000–4999 belong to applications.
 * The client stops reconnecting on it and sends the person to sign in; any
 * other close is a network problem and is retried.
 */
export const SESSION_ENDED_CLOSE = 4401;
//# sourceMappingURL=realtime.js.map